"use client";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { useTranslations } from "next-intl";

import { Button } from "@ui/components/core/Button";
import { BaseLink } from "@ui/components/core/Link";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconArrowUpRight from "@ui/central-icons/icons/IconArrowUpRight";
import IconChevronLargeLeft from "@ui/central-icons/icons/IconChevronLargeLeft";
import IconChevronLargeRight from "@ui/central-icons/icons/IconChevronLargeRight";
import IconChevronLeftMedium from "@ui/central-icons/icons/IconChevronLeftMedium";
import IconChevronLeftSmall from "@ui/central-icons/icons/IconChevronLeftSmall";
import IconChevronRightMedium from "@ui/central-icons/icons/IconChevronRightMedium";
import IconChevronRightSmall from "@ui/central-icons/icons/IconChevronRightSmall";
import { EventName } from "@mw/shared/analyticsEventNames";

import type { VoiceWithPreview } from "@ui/components/demos/VoicesWithTTS/types";
import { appUrls } from "@/constants";
import { pxToRem } from "@ui/lib/utils";

const DESKTOP_SUMMARY_CARD_WIDTH_PX = 260;
const DESKTOP_SUMMARY_CARD_GAP_PX = 80;

function getBoundedIndex(index: number, total: number) {
  if (index < 0 || index >= total) return undefined;
  return index;
}

function getVisibleTriplet(activeIndex: number, total: number) {
  return [
    getBoundedIndex(activeIndex - 1, total),
    getBoundedIndex(activeIndex, total),
    getBoundedIndex(activeIndex + 1, total),
  ];
}

function getVoiceTitle(voice: VoiceWithPreview) {
  return voice.category || voice.name;
}

function getVoiceDescription(voice: VoiceWithPreview) {
  return voice.shortDescription || voice.description || "";
}

function VoiceSummaryCard({
  voice,
  emphasized,
  widthPx,
  isCompact,
}: {
  voice: VoiceWithPreview | undefined;
  emphasized: boolean;
  widthPx: number;
  isCompact: boolean;
}) {
  const title = voice ? getVoiceTitle(voice) : "";
  const description = voice ? getVoiceDescription(voice) : "";

  return (
    <div
      className={clsx(
        "tw-box-border tw-flex tw-flex-none tw-flex-col tw-items-center tw-px-4",
        isCompact
          ? "tw-h-20 tw-justify-center tw-gap-2 tw-py-0"
          : "tw-gap-4 tw-py-2 md:tw-min-h-20 md:tw-justify-center md:tw-py-0",
      )}
      style={{ width: pxToRem(widthPx) }}
    >
      <p
        className={clsx(
          "tw-text-center tw-text-balance tw-transition-colors tw-duration-200",
          isCompact ? "tw-type-base tw-py-1" : "tw-type-xl",
          emphasized ? "tw-text-black" : "tw-text-gray-500",
        )}
      >
        {isCompact && voice?.link ? (
          <BaseLink
            href={voice.link}
            aria-disabled={!emphasized}
            className={clsx(
              "tw-inline-flex tw-items-center tw-rounded-sm tw-outline-none focus-visible:tw-outline-focus tw-transition-colors tw-duration-200",
              emphasized
                ? "hover:tw-underline hover:tw-decoration-1.5 hover:tw-underline-offset-2"
                : "tw-pointer-events-none",
            )}
            tabIndex={emphasized ? undefined : -1}
          >
            {title}
            <span
              aria-hidden
              className={clsx(
                "tw-inline-flex tw-flex-none tw-justify-center tw-overflow-hidden tw-transition-all tw-duration-200",
                emphasized ? "tw-w-3 tw-ml-1.5 tw-opacity-100" : "tw-w-0 tw-ml-0 tw-opacity-0",
              )}
            >
              <IconArrowUpRight className={clsx(iconStrokeFix, "tw-flex-none tw-size-3 tw-relative tw-top-0.25")} />
            </span>
          </BaseLink>
        ) : (
          title
        )}
      </p>
      <p
        className={clsx(
          "tw-text-center tw-text-balance tw-transition-colors tw-duration-200",
          isCompact ? "tw-type-2xs tw-py-1" : "tw-type-xs",
          emphasized ? "tw-text-gray-500" : "tw-text-gray-400",
        )}
      >
        {description}
      </p>
    </div>
  );
}

function MobileVoiceSummary({ voice, isCompact }: { voice: VoiceWithPreview; isCompact: boolean }) {
  const title = getVoiceTitle(voice);
  const description = getVoiceDescription(voice);

  return (
    <div
      className={clsx(
        "tw-flex tw-w-full tw-max-w-[16.25rem] tw-flex-col tw-items-center",
        isCompact ? "tw-h-20 tw-justify-center tw-gap-2" : "tw-gap-4",
      )}
    >
      <p
        className={clsx(
          "tw-text-center tw-text-black tw-text-pretty",
          isCompact ? "tw-type-base tw-py-1" : "tw-type-xl",
        )}
      >
        {isCompact && voice.link ? (
          <BaseLink
            href={voice.link}
            className='tw-outline-none focus-visible:tw-outline-focus tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-sm hover:tw-underline hover:tw-decoration-1.5 hover:tw-underline-offset-2'
          >
            {title}
            <IconArrowUpRight className={clsx(iconStrokeFix, "tw-flex-none tw-size-3 tw-relative tw-top-0.25")} />
          </BaseLink>
        ) : (
          title
        )}
      </p>
      <p
        className={clsx(
          "tw-text-center tw-text-gray-500 tw-text-balance",
          isCompact ? "tw-type-2xs tw-py-1" : "tw-type-xs",
        )}
      >
        {description}
      </p>
    </div>
  );
}

export interface OrbCarouselFooterProps {
  voices: VoiceWithPreview[];
  activeIndex: number;
  slideDirection: 1 | -1;
  onPrevious: () => void;
  onNext: () => void;
  hideUseVoiceCta?: boolean;
  summaryCardWidthPx?: number;
  summaryCardGapPx?: number;
  variant?: "default" | "compact";
}

export function OrbCarouselFooter({
  voices,
  activeIndex,
  slideDirection,
  onPrevious,
  onNext,
  hideUseVoiceCta = false,
  summaryCardWidthPx = DESKTOP_SUMMARY_CARD_WIDTH_PX,
  summaryCardGapPx = DESKTOP_SUMMARY_CARD_GAP_PX,
  variant = "default",
}: OrbCarouselFooterProps) {
  const isCompact = variant === "compact";
  const tVoicesSlider = useTranslations("ClientKeys.VoicesSlider");
  const activeVoice = voices[activeIndex];
  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < voices.length - 1;
  const [displayIndex, setDisplayIndex] = useState(activeIndex);
  const [trackOffset, setTrackOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const rafRef = useRef(0);
  const scrollLockRef = useRef(false);
  const summaryCardWidth = summaryCardWidthPx;
  const trackStep = summaryCardWidth + summaryCardGapPx;

  const handlePrevious = () => {
    if (scrollLockRef.current) return;
    scrollLockRef.current = true;
    onPrevious();
  };
  const handleNext = () => {
    if (scrollLockRef.current) return;
    scrollLockRef.current = true;
    onNext();
  };

  const visibleTriplet = useMemo(() => getVisibleTriplet(displayIndex, voices.length), [displayIndex, voices.length]);
  const preloadLeftIndex = useMemo(
    () => getBoundedIndex(displayIndex - 2, voices.length),
    [displayIndex, voices.length],
  );
  const preloadRightIndex = useMemo(
    () => getBoundedIndex(displayIndex + 2, voices.length),
    [displayIndex, voices.length],
  );

  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useLayoutEffect(() => {
    if (voices.length <= 1) {
      setDisplayIndex(activeIndex);
      setTrackOffset(0);
      setIsAnimating(false);
      return;
    }

    if (activeIndex === displayIndex) return;

    cancelAnimationFrame(rafRef.current);

    // Jump: update content and offset with no transition (committed before paint)
    setDisplayIndex(activeIndex);
    setTrackOffset(slideDirection * trackStep);
    setIsAnimating(false);

    // Settle: after the browser paints the jump state, enable transition and animate to 0
    rafRef.current = requestAnimationFrame(() => {
      setIsAnimating(true);
      setTrackOffset(0);
      setTimeout(() => {
        scrollLockRef.current = false;
      }, 360);
    });
  }, [activeIndex, displayIndex, slideDirection, trackStep, voices.length]);

  if (!activeVoice) return null;

  return (
    <div>
      <div className={clsx("tw-px-4 tw-pb-4", isCompact ? "lg:tw-hidden" : "md:tw-hidden")}>
        {isCompact ? (
          <div
            className='tw-grid tw-w-full tw-items-center tw-gap-x-2 sm:tw-gap-x-8 tw-gap-y-0 -tw-mx-4'
            style={{ gridTemplateColumns: "2.75rem minmax(0,1fr) 2.75rem", width: "calc(100% + 2rem)" }}
          >
            <div className='tw-flex tw-justify-start tw-row-span-2 tw-self-center'>
              <Button
                aria-label={tVoicesSlider("previous_voice")}
                className={clsx(
                  "tw-group tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity tw-z-30",
                  canGoPrev ? "tw-text-gray-400" : "tw-pointer-events-none tw-opacity-0",
                )}
                disabled={!canGoPrev}
                icon={<IconChevronLargeLeft className={clsx(iconStrokeFix, "tw-size-4 group-hover:tw-text-black")} />}
                onClick={handlePrevious}
                variant='ghost'
              />
            </div>

            <div className='tw-flex tw-justify-center'>
              <MobileVoiceSummary voice={activeVoice} isCompact />
            </div>

            <div className='tw-flex tw-justify-end tw-row-span-2 tw-self-center'>
              <Button
                aria-label={tVoicesSlider("next_voice")}
                className={clsx(
                  "tw-group tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity tw-z-30",
                  canGoNext ? "tw-text-gray-400" : "tw-pointer-events-none tw-opacity-0",
                )}
                disabled={!canGoNext}
                icon={<IconChevronLargeRight className={clsx(iconStrokeFix, "tw-size-4 group-hover:tw-text-black")} />}
                onClick={handleNext}
                variant='ghost'
              />
            </div>

            {!hideUseVoiceCta && (
              <div className='tw-flex tw-min-w-0 tw-justify-center tw-col-start-2 tw-mt-4'>
                <LinkTracked
                  asButton
                  buttonVariants={{ variant: "elevated", size: "lg" }}
                  className='tw-whitespace-nowrap'
                  href={activeVoice.link ?? appUrls.voiceLibrary}
                  eventName={EventName.mw_tts_voice_library_explore_clicked}
                  eventProperties={{ source_component: "slider_generate_tts_omnibox", voice_id: activeVoice.voiceId }}
                >
                  {tVoicesSlider("use_voice")}
                </LinkTracked>
              </div>
            )}
          </div>
        ) : (
          <div className='tw-flex tw-flex-col tw-items-center tw-gap-7'>
            <MobileVoiceSummary voice={activeVoice} isCompact={false} />

            <div
              className='tw-grid tw-w-full tw-items-center tw-gap-4'
              style={{ gridTemplateColumns: "2.75rem minmax(0,1fr) 2.75rem" }}
            >
              <div className='tw-flex tw-justify-start'>
                <Button
                  aria-label={tVoicesSlider("previous_voice")}
                  className={clsx(
                    "tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity",
                    !canGoPrev && "tw-pointer-events-none tw-opacity-0",
                  )}
                  disabled={!canGoPrev}
                  icon={<IconChevronLeftSmall className={clsx(iconStrokeFix, "tw-size-5")} />}
                  onClick={handlePrevious}
                  variant='elevated'
                />
              </div>

              <div className='tw-flex tw-min-w-0 tw-justify-center'>
                <LinkTracked
                  asButton
                  buttonVariants={{ variant: "elevated", size: "lg" }}
                  className='tw-whitespace-nowrap'
                  href={activeVoice.link ?? appUrls.voiceLibrary}
                  eventName={EventName.mw_tts_voice_library_explore_clicked}
                  eventProperties={{ source_component: "slider_generate_tts_omnibox", voice_id: activeVoice.voiceId }}
                >
                  {tVoicesSlider("use_voice")}
                </LinkTracked>
              </div>

              <div className='tw-flex tw-justify-end'>
                <Button
                  aria-label={tVoicesSlider("next_voice")}
                  className={clsx(
                    "tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity",
                    !canGoNext && "tw-pointer-events-none tw-opacity-0",
                  )}
                  disabled={!canGoNext}
                  icon={<IconChevronRightSmall className={clsx(iconStrokeFix, "tw-size-5")} />}
                  onClick={handleNext}
                  variant='elevated'
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={clsx(
          "tw-hidden",
          isCompact ? "lg:tw-block lg:tw-px-8 lg:tw-pb-8" : "md:tw-block md:tw-px-8 md:tw-pb-8",
        )}
      >
        <div className='tw-flex tw-flex-col tw-items-center tw-gap-2'>
          <div className='tw-relative tw-min-h-5'>
            <div
              className='tw-relative tw-overflow-hidden'
              style={{ width: pxToRem(summaryCardWidth * 3 + summaryCardGapPx * 2) }}
            >
              <div
                className={clsx("tw-relative tw-flex", isAnimating && "tw-will-change-transform")}
                style={{
                  gap: pxToRem(summaryCardGapPx),
                  transform: `translateX(${pxToRem(trackOffset)})`,
                  transitionProperty: isAnimating ? "transform" : "none",
                  transitionDuration: "360ms",
                  transitionTimingFunction: "ease",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: pxToRem(-trackStep),
                    width: pxToRem(summaryCardWidth),
                  }}
                >
                  <VoiceSummaryCard
                    voice={preloadLeftIndex === undefined ? undefined : voices[preloadLeftIndex]}
                    emphasized={false}
                    widthPx={summaryCardWidth}
                    isCompact={isCompact}
                  />
                </div>

                {visibleTriplet.map((voiceIndex, position) => (
                  <VoiceSummaryCard
                    key={
                      voiceIndex === undefined
                        ? `empty-${displayIndex}-${position}`
                        : `${voices[voiceIndex]?.voiceId ?? voiceIndex}-${voiceIndex}`
                    }
                    voice={voiceIndex === undefined ? undefined : voices[voiceIndex]}
                    emphasized={position === 1}
                    widthPx={summaryCardWidth}
                    isCompact={isCompact}
                  />
                ))}

                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: pxToRem(trackStep * 3),
                    width: pxToRem(summaryCardWidth),
                  }}
                >
                  <VoiceSummaryCard
                    voice={preloadRightIndex === undefined ? undefined : voices[preloadRightIndex]}
                    emphasized={false}
                    widthPx={summaryCardWidth}
                    isCompact={isCompact}
                  />
                </div>
              </div>
              <div className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-w-6 tw-bg-gradient-to-r tw-from-cream tw-to-transparent' />
              <div className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-w-6 tw-bg-gradient-to-l tw-from-cream tw-to-transparent' />
            </div>

            <div className='tw-pointer-events-none tw-absolute tw-inset-0'>
              <div
                className='tw-pointer-events-auto tw-absolute tw-top-1/2'
                style={{
                  left: pxToRem(summaryCardWidth + summaryCardGapPx / 2),
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Button
                  aria-label={tVoicesSlider("previous_voice")}
                  className={clsx(
                    "tw-group tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity",
                    isCompact ? "tw-size-10" : "tw-size-11",
                    !canGoPrev && "tw-pointer-events-none tw-opacity-0",
                  )}
                  disabled={!canGoPrev}
                  icon={
                    <IconChevronLeftMedium
                      className={clsx(iconStrokeFix, "tw-size-5 tw-text-gray-400 group-hover:tw-text-black")}
                    />
                  }
                  onClick={handlePrevious}
                  variant='secondary'
                />
              </div>

              <div
                className='tw-pointer-events-auto tw-absolute tw-top-1/2'
                style={{
                  left: pxToRem(summaryCardWidth * 2 + summaryCardGapPx * 1.5),
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Button
                  aria-label={tVoicesSlider("next_voice")}
                  className={clsx(
                    "tw-group tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity",
                    isCompact ? "tw-size-10" : "tw-size-11",
                    !canGoNext && "tw-pointer-events-none tw-opacity-0",
                  )}
                  disabled={!canGoNext}
                  icon={
                    <IconChevronRightMedium
                      className={clsx(iconStrokeFix, "tw-size-5 tw-text-gray-400 group-hover:tw-text-black")}
                    />
                  }
                  onClick={handleNext}
                  variant='secondary'
                />
              </div>
            </div>
          </div>

          {!hideUseVoiceCta && (
            <div>
              <LinkTracked
                asButton
                buttonVariants={{ variant: "elevated", size: "md" }}
                href={activeVoice.link ?? appUrls.voiceLibrary}
                eventName={EventName.mw_tts_voice_library_explore_clicked}
                eventProperties={{ source_component: "slider_generate_tts_omnibox", voice_id: activeVoice.voiceId }}
              >
                {tVoicesSlider("use_voice")}
              </LinkTracked>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
